import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  operations as sitesOperations,
  selectors as sitesSelectors,
} from 'store/sitesManager'
import Unauthorized from 'secondstep-components/src/components/Unauthorized'
import UserManagement from './component'
import {
  inputModeType,
  siteLicenseChecker,
  UpdateSiteUserNotifications,
} from 'lib/util'
import { FILTER_USER_MAP } from './constants'
import {
  FILTER_CONFIG,
  getFilterOptionFromQuery,
  calculateUserCounts,
} from './data'

export const UserManagementContainer = ({ pushTo }) => {
  const dispatch = useDispatch()
  const { siteId: siteIdParam } = useParams()
  const siteId = siteIdParam ? parseInt(siteIdParam, 10) : null

  // Redux state - base state only
  const {
    addBulkUsersResponse,
    hasUnassignedLicenses,
    isAddBulkUsersCompleted,
    isLoading,
    isUpdating,
    location,
    sites,
  } = useSelector(state => {
    const {
      licenseManager,
      router,
      sitesManager: {
        sites: { data: sites, isLoading: isSitesLoading },
        siteUsers: {
          addBulkUsersResponse,
          isLoading: isSiteUsersLoading,
          isUpdating,
          isAddBulkUsersCompleted,
        },
        remindUsers: { isLoading: isSending },
      },
    } = state

    const hasUnassignedLicenses =
      licenseManager?.userLicenses?.unassignedLicenses?.length > 0
    const isLoading = isSitesLoading || isSiteUsersLoading || isSending
    const { location } = router

    return {
      addBulkUsersResponse,
      hasUnassignedLicenses,
      isAddBulkUsersCompleted,
      isLoading,
      isUpdating,
      location,
      sites,
    }
  })

  // Compute site from sites array
  const site = useMemo(() => {
    return !siteId ? sites?.[0] : sites?.find(s => s.id === siteId)
  }, [siteId, sites])

  // Compute siteUsers based on site (needs state access, so use useSelector)
  const siteUsers = useSelector(state => {
    if (!site || !siteId) return null
    const { hasK8License } = siteLicenseChecker(site)
    return sitesSelectors.selectSiteUsersBySiteId(state, siteId, hasK8License)
  })

  // Local state
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [sendInvitesModalOpen, setSendInvitesModalOpen] = useState(false)
  const [selectedFilterOption, setSelectedFilterOption] = useState({
    key: 'all',
    name: FILTER_USER_MAP.all,
  })

  // Derived values
  const { hasK8License, hasSelaLicense } = useMemo(
    () => (site ? siteLicenseChecker(site) : {}),
    [site],
  )

  const activeUsers = useMemo(
    () => (siteUsers ? siteUsers.filter(user => user.isActive) : []),
    [siteUsers],
  )

  // Calculate user counts and filter options
  const {
    userCounts,
    userFilterOptions,
    userInviteDialogCounts,
  } = useMemo(() => {
    if (!activeUsers.length) {
      return {
        userCounts: null,
        userFilterOptions: [],
        userInviteDialogCounts: null,
      }
    }

    const newUserCounts = calculateUserCounts(activeUsers)

    const filterUserCounts = {
      all: newUserCounts.all,
      admin: newUserCounts.admin,
      teacher: newUserCounts.teacher,
      leaderTeam: hasSelaLicense ? newUserCounts.leaderTeam : undefined,
      pending: newUserCounts.pending,
      noClasses: newUserCounts.noClasses,
    }

    // Remove undefined values
    Object.keys(filterUserCounts).forEach(key => {
      if (filterUserCounts[key] === undefined) {
        delete filterUserCounts[key]
      }
    })

    const filterOptions = Object.entries(filterUserCounts).map(
      ([key, count]) => ({
        name: `${FILTER_USER_MAP[key]} (${count})`,
        key,
      }),
    )

    const inviteDialogCounts = {
      pending: newUserCounts.pending,
      all: newUserCounts.all,
      admin: newUserCounts.admin,
      leaderTeam: newUserCounts.leaderTeam,
      teacher: newUserCounts.teacher,
    }

    return {
      userCounts: newUserCounts,
      userFilterOptions: filterOptions,
      userInviteDialogCounts: inviteDialogCounts,
    }
  }, [activeUsers, hasSelaLicense])

  // Filtered users based on selected filter
  const users = useMemo(() => {
    if (!siteUsers || !selectedFilterOption?.key) {
      return activeUsers
    }

    const config = FILTER_CONFIG[selectedFilterOption.key]
    return config ? config.filter(siteUsers) : activeUsers
  }, [siteUsers, activeUsers, selectedFilterOption?.key])

  // Sites filtered by admin claim
  const adminSites = useMemo(
    () => (sites ? sites.filter(s => s.claims?.includes('admin')) : []),
    [sites],
  )

  // Initialize: Redirect if no siteId
  useEffect(() => {
    if (!siteId && site?.id) {
      pushTo(`/user-management/${site.id}`)
    }
  }, [siteId, site?.id, pushTo])

  // Action creators
  const fetchSiteUsers = useCallback(
    siteId => dispatch(sitesOperations.fetchSiteUsers(siteId)),
    [dispatch],
  )

  const fetchK8SiteUsers = useCallback(
    siteId => dispatch(sitesOperations.fetchK8SiteUsers(siteId)),
    [dispatch],
  )

  // Track the last siteId we fetched users for
  const lastFetchedSiteIdRef = useRef(null)

  // Fetch site users when site changes
  useEffect(() => {
    if (!site?.id) return

    // Fetch if siteId changed or if siteUsers don't exist for current site
    const siteIdChanged = lastFetchedSiteIdRef.current !== site.id
    const siteUsersMissing = siteUsers === null || siteUsers === undefined

    if (siteIdChanged || siteUsersMissing) {
      lastFetchedSiteIdRef.current = site.id
      fetchSiteUsers(site.id)
      if (hasK8License) {
        fetchK8SiteUsers(site.id)
      }
    }
  }, [site?.id, siteUsers, hasK8License, fetchSiteUsers, fetchK8SiteUsers])

  // Set initial filter option from URL query params
  useEffect(() => {
    if (!siteUsers || activeUsers.length === 0) return

    const filterOption = getFilterOptionFromQuery(location?.query)
    setSelectedFilterOption(filterOption)
  }, [siteUsers, activeUsers.length, location?.query])

  // Action creators
  const createSiteUser = useCallback(
    (user, sendNotification, siteData) =>
      dispatch(
        sitesOperations.createSiteUser(user, sendNotification, siteData),
      ),
    [dispatch],
  )

  const createSiteUsers = useCallback(
    (siteId, users, sendNotification) =>
      dispatch(
        sitesOperations.createSiteUsers(siteId, users, sendNotification),
      ),
    [dispatch],
  )

  const remindSiteUser = useCallback(
    siteUser => dispatch(sitesOperations.remindSiteUser(siteUser)),
    [dispatch],
  )

  const remindSiteUsers = useCallback(
    (siteId, params) =>
      dispatch(sitesOperations.remindSiteUsers(siteId, params)),
    [dispatch],
  )

  const resetBulkAddUsers = useCallback(
    () => dispatch(sitesOperations.resetBulkAddUsers()),
    [dispatch],
  )

  const updateSiteUser = useCallback(
    (user, notification) =>
      dispatch(sitesOperations.updateSiteUser(user, notification)),
    [dispatch],
  )

  // Event handlers
  const filterSiteUsers = useCallback(
    event => {
      const filterKey = event?.key || event
      const config = FILTER_CONFIG[filterKey]

      if (!config || !site?.id) return

      const siteUrl = `/user-management/${site.id}`
      setSelectedFilterOption({
        key: filterKey,
        name: FILTER_USER_MAP[filterKey],
      })
      pushTo(`${siteUrl}${config.query}`)
    },
    [site?.id, pushTo],
  )

  const onSiteChange = useCallback(
    e => {
      const newSiteId = e?.value?.id
      if (newSiteId && newSiteId !== siteId) {
        pushTo(`/user-management/${newSiteId}`)
      }
    },
    [siteId, pushTo],
  )

  const redirectToUserManagement = useCallback(() => {
    if (siteId) {
      pushTo(`/user-management/${siteId}`)
    }
  }, [siteId, pushTo])

  const redirectToLicenseManagement = useCallback(
    () => pushTo('/license-management'),
    [pushTo],
  )

  const handleSendInvites = useCallback(
    ({ all, pending, ...roles }) => {
      if (!site?.id) return

      const sendInvObj = { pendingUsers: !!pending }

      if (!all && !pending && Object.values(roles).every(role => !role)) {
        setSendInvitesModalOpen(false)
        return
      }

      if (all || pending) {
        remindSiteUsers(site.id, sendInvObj)
        setSendInvitesModalOpen(false)
        return
      }

      const sendToRoles = Object.entries(roles)
        .filter(
          ([role, value]) => value && role !== 'all' && role !== 'pending',
        )
        .map(([role]) =>
          role === 'leaderTeam'
            ? 'Leader Team'
            : `${role.charAt(0).toUpperCase()}${role.slice(1)}`,
        )

      if (sendToRoles.length > 0) {
        sendInvObj.roles = sendToRoles
      }

      remindSiteUsers(site.id, sendInvObj)
      setSendInvitesModalOpen(false)
    },
    [site?.id, remindSiteUsers],
  )

  // Simple state setters don't need memoization
  const handleOpenSendInvitesModal = () => setSendInvitesModalOpen(true)
  const handleCloseSendInvitesModal = () => setSendInvitesModalOpen(false)
  const handleOpenAddUsersModal = () => setIsAddUserModalOpen(true)

  const handleSubmitAddUsers = useCallback(
    async (emails, role, sendNotification, inputMode) => {
      if (!siteId) return

      const addSiteUsers = emails.map(email => ({
        inviteEmail: email?.trim(),
        isActive: true,
        roles: [role],
        siteId,
      }))

      if (inputMode === inputModeType.bulk) {
        await createSiteUsers(siteId, addSiteUsers, sendNotification)
      } else {
        await createSiteUser(addSiteUsers[0], sendNotification, site)
        resetBulkAddUsers()
        setIsAddUserModalOpen(false)
      }
    },
    [siteId, site, createSiteUser, createSiteUsers, resetBulkAddUsers],
  )

  const handleCloseAddUsersModal = useCallback(() => {
    resetBulkAddUsers()
    redirectToUserManagement()
    setIsAddUserModalOpen(false)
  }, [resetBulkAddUsers, redirectToUserManagement])

  const handleRemindEmail = remindSiteUser

  const handleGrantNewRole = useCallback(
    (siteUser, newRole) => {
      updateSiteUser(
        { ...siteUser, roles: [newRole] },
        UpdateSiteUserNotifications.grantNewRole,
      )
    },
    [updateSiteUser],
  )

  const handleCancelUserAccess = useCallback(
    siteUser => {
      updateSiteUser(
        { ...siteUser, isActive: false },
        UpdateSiteUserNotifications.removeUser,
      )
    },
    [updateSiteUser],
  )

  // Unauthorized state - keep in container as it's an authorization/data concern
  // Only show Unauthorized when sites have loaded (not loading) and exist, but the specific site is not found
  // Check: if we have a siteId in the URL but the site doesn't exist in our sites array
  const hasSiteIdButSiteNotFound = siteId !== null && !site
  if (!isLoading && sites && sites.length > 0 && hasSiteIdButSiteNotFound) {
    return <Unauthorized location={location} />
  }

  // Pass all props to component - let component handle loading and empty states
  return (
    <UserManagement
      addBulkUsersResponse={addBulkUsersResponse}
      allActiveUsersCount={userCounts}
      filterSiteUsers={filterSiteUsers}
      handleCancelUserAccess={handleCancelUserAccess}
      handleCloseAddUsersModal={handleCloseAddUsersModal}
      handleCloseSendInvitesModal={handleCloseSendInvitesModal}
      handleGrantNewRole={handleGrantNewRole}
      handleOpenAddUsersModal={handleOpenAddUsersModal}
      handleOpenSendInvitesModal={handleOpenSendInvitesModal}
      handleRemindEmail={handleRemindEmail}
      handleSendInvites={handleSendInvites}
      handleSubmitAddUsers={handleSubmitAddUsers}
      hasK8License={hasK8License}
      hasSelaLicense={hasSelaLicense}
      hasUnassignedLicenses={hasUnassignedLicenses}
      isAddBulkUsersCompleted={isAddBulkUsersCompleted}
      isAddUserModalOpen={isAddUserModalOpen}
      isLoading={isLoading}
      isSendInvitesModalOpen={sendInvitesModalOpen}
      isUpdating={isUpdating}
      onSiteChange={onSiteChange}
      redirectToLicenseManagement={redirectToLicenseManagement}
      selectedFilterOption={selectedFilterOption}
      selectedSite={site}
      selectedSiteUsers={users}
      siteUsers={siteUsers}
      sites={adminSites}
      updateSiteUser={updateSiteUser}
      userFilterOptions={userFilterOptions}
      userInviteDialogCounts={userInviteDialogCounts}
    />
  )
}

UserManagementContainer.propTypes = {
  pushTo: PropTypes.func.isRequired,
}

export default UserManagementContainer
